import React, { useState, useEffect, useRef } from "react";

const MultiSelectWithApply = ({
    label = "Label",
    options = [],
    selected = [],
    onChange,
    width = 250,
}) => {
    const [open, setOpen] = useState(false);
    const [tempSelected, setTempSelected] = useState([]);
    const containerRef = useRef(null);

    const getIds = (arr) => arr.map((item) => item.id);
    const tempSelectedIds = getIds(tempSelected);

    const isSelected = (option) => tempSelectedIds.includes(option.id);

    // Sync tempSelected with selected when dropdown opens
    useEffect(() => {
        if (open) {
            setTempSelected([...selected]);
        }
    }, [open, selected]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleOption = (option) => {
        const alreadySelected = isSelected(option);
        const newSelection = alreadySelected
            ? tempSelected.filter((item) => item.id !== option.id)
            : [...tempSelected, option];
        setTempSelected(newSelection);
    };

    const handleApply = () => {
        onChange(tempSelected);
        setOpen(false);
    };

    const handleClear = () => {
        setTempSelected([]);
    };

    const getDisplayText = () => {
        if (selected.length === 0) return "Select...";
        if (selected.length === 1) return selected[0].name;
        return `${selected.length} selected`;
    };

    return (
        <div className="relative inline-block text-left" style={{ width }} ref={containerRef}>
            <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
            <button
                onClick={() => setOpen(!open)}
                className="inline-flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none"
                style={{ fontSize: "0.875rem", minHeight: "40px" }}
            >
                <span className="truncate">{getDisplayText()}</span>
                <svg className="ml-2 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.586l3.71-4.356a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            {open && (
                <div className="absolute z-10 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                    {/* Options list */}
                    <div className="overflow-auto py-1" style={{ maxHeight: "500px" }}>
                        {options.map((option) => (
                            <button
                                key={option.id}
                                onClick={() => toggleOption(option)}
                                className={`flex w-full items-center px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                                    isSelected(option)
                                        ? "bg-gray-100 font-semibold text-gray-900"
                                        : "text-gray-700"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected(option)}
                                    readOnly
                                    className="mr-2"
                                />
                                {option.name}
                            </button>
                        ))}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center justify-between border-t border-gray-200 px-3 py-2">
                        <button
                            onClick={handleClear}
                            className="text-sm text-gray-500 hover:text-gray-700"
                        >
                            Clear
                        </button>
                        <button
                            onClick={handleApply}
                            className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                        >
                            Done
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectWithApply;
